import type { WoodFinishRow } from '@estimator/shared';

interface WoodFinishBreakdownProps {
  rows: WoodFinishRow[];
}

function fmt(n: number): string {
  return n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
}

/**
 * Per-panel applied-finish detail for the expanded cost panel. A row with no
 * resolved profile shows amber and costs nothing, rather than silently
 * contributing zero.
 */
export default function WoodFinishBreakdown({ rows }: WoodFinishBreakdownProps) {
  if (rows.length === 0) return null;

  const total = rows.reduce((s, r) => s + r.cost, 0);

  return (
    <div className="px-4 py-2" style={{ borderTop: '1px solid var(--border)' }}>
      <div className="text-[10px] uppercase mb-1" style={{ color: '#e67e22' }}>
        Finishing
      </div>
      {rows.map((row, i) => (
        <div key={`${row.panel_name}-${i}`} className="flex items-center gap-3 text-xs py-0.5">
          <span className="flex-1 truncate" style={{ color: 'var(--text-primary)' }}>
            {row.panel_name}
          </span>
          <span className="w-12 text-right" style={{ color: 'var(--text-muted)' }}>
            {row.pieces} pc
          </span>
          <span className="w-16 text-right" style={{ color: 'var(--text-muted)' }}>
            {row.sqft.toFixed(1)} SF
          </span>
          <span className="w-10 text-right" style={{ color: 'var(--text-muted)' }}>
            ×{row.sides}
          </span>
          <span className="w-44 truncate text-right" style={{ color: 'var(--text-secondary)' }}>
            {row.warning
              ? row.warning
              : `${row.finish_name} @ $${row.cost_per_sqft.toFixed(2)}/SF`}
          </span>
          <span
            className="w-20 text-right font-semibold"
            style={{ color: row.warning ? '#e0a030' : 'var(--text-primary)' }}
          >
            {fmt(row.cost)}
          </span>
        </div>
      ))}
      <div
        className="flex items-center justify-end gap-3 text-xs pt-1 mt-1"
        style={{ borderTop: '1px solid var(--border)' }}
      >
        <span style={{ color: 'var(--text-secondary)' }}>Finishing total</span>
        <span className="w-20 text-right font-semibold" style={{ color: 'var(--text-primary)' }}>
          {fmt(total)}
        </span>
      </div>
    </div>
  );
}
